import api from './client'

export interface MediaAsset {
  id: string
  type: 'PHOTO' | 'VIDEO' | 'AUDIO'
  status: 'PENDING' | 'PROCESSING' | 'READY' | 'ERROR'
  mimeType: string
  sizeBytes: string
  originalKey: string
  processedKey?: string
  thumbnailKey?: string
  viewUrl?: string
  thumbUrl?: string
  capturedAt?: string
  createdAt: string
  depthM?: number | null
  waterTempC?: number | null
  visibilityM?: number | null
  notes?: string | null
  diveId?: string | null
}

// ── Species Library Asset (divesites service) ─────────────────────────────────

export type EncyclopediaStatus = 'PRIVATE' | 'PENDING_REVIEW' | 'ENCYCLOPEDIA'

export interface LibraryAsset {
  asset_id: string
  url: string
  content_type: string
  file_size_bytes: number | null
  width: number | null
  height: number | null
  encyclopedia_status: EncyclopediaStatus
  species_id: string | null
  species_name_identified: string | null
  identification_score: number | null
  upload_date: string | null
  consent_granted: boolean
  is_duplicate?: boolean
  exif: {
    taken_at: string | null
    lat: number | null
    lng: number | null
    altitude_m: number | null
    camera_make: string | null
    camera_model: string | null
    focal_length_mm: number | null
    exposure_time: string | null
    aperture: string | null
    iso: number | null
  }
}

export interface InatCandidate {
  scientific_name: string
  common_name_en: string
  inat_taxon_id: string
  rank: string
  combined_score: number
  frequency_score: number
  vision_score: number
}

export interface EncyclopediaAsset extends LibraryAsset {
  owner_user_id: string
}

// ── Field Journal API (divesites service) ────────────────────────────────────

export interface FieldJournalSpeciesPayload {
  scientific_name: string
  common_name: string
  count: number
  behavior: string
  notes: string
  photo_asset_id?: string | null
  photo_url?: string | null
  inat_score?: number | null
}

export interface FieldJournalPayload {
  date: string
  location_name: string
  lat?: number | null
  lng?: number | null
  depth_m?: number | null
  water_temp_c?: number | null
  salinity_psu?: number | null
  visibility_m?: number | null
  current: string
  notes: string
  species: FieldJournalSpeciesPayload[]
}

export interface FieldJournalEntryResponse {
  id: string
  date: string
  locationName: string
  lat: string
  lng: string
  depth: string
  waterTemp: string
  salinity: string
  visibility: string
  current: string
  notes: string
  createdAt: number
  species: Array<{
    id: string
    scientificName: string
    commonName: string
    count: number
    behavior: string
    notes: string
    photoAssetId: string | null
    photoUrl: string | null
    inatScore: number | null
  }>
}

export const fieldJournalApi = {
  list(params?: { limit?: number; offset?: number }): Promise<{ items: FieldJournalEntryResponse[]; total: number }> {
    const qs = new URLSearchParams()
    if (params?.limit) qs.set('limit', String(params.limit))
    if (params?.offset) qs.set('offset', String(params.offset))
    return api.get<{ items: FieldJournalEntryResponse[]; total: number }>(`/v1/divesites/field-journal${qs.toString() ? '?' + qs : '/'}`).then(r => r.data)
  },

  create(payload: FieldJournalPayload): Promise<FieldJournalEntryResponse> {
    return api.post<FieldJournalEntryResponse>('/v1/divesites/field-journal', payload).then(r => r.data)
  },

  delete(id: string): Promise<void> {
    return api.delete(`/v1/divesites/field-journal/${id}`).then(() => undefined)
  },
}

export const libraryApi = {
  upload(file: File, consent = false): Promise<LibraryAsset> {
    const form = new FormData()
    form.append('file', file)
    form.append('consent', String(consent))
    return api.post<LibraryAsset>('/v1/divesites/media/upload', form).then(r => r.data)
  },

  list(params?: { encyclopedia_status?: EncyclopediaStatus; limit?: number; offset?: number }): Promise<{ items: LibraryAsset[]; total: number }> {
    const qs = new URLSearchParams()
    if (params?.encyclopedia_status) qs.set('encyclopedia_status', params.encyclopedia_status)
    if (params?.limit) qs.set('limit', String(params.limit))
    if (params?.offset) qs.set('offset', String(params.offset))
    return api.get<{ items: LibraryAsset[]; total: number }>(`/v1/divesites/media/mine${qs.toString() ? '?' + qs : ''}`).then(r => r.data)
  },

  get(assetId: string): Promise<LibraryAsset> {
    return api.get<LibraryAsset>(`/v1/divesites/media/${assetId}`).then(r => r.data)
  },

  delete(assetId: string): Promise<void> {
    return api.delete(`/v1/divesites/media/${assetId}`).then(() => undefined)
  },

  identify(assetId: string): Promise<{ asset_id: string; candidates: InatCandidate[]; top_match: InatCandidate | null; source: string }> {
    return api.post(`/v1/divesites/media/${assetId}/identify`).then(r => r.data)
  },

  contribute(assetId: string, data: { consent: true; notes?: string; depth_m?: number; location_name?: string }): Promise<{ asset_id: string; review_id: string; status: string; message: string }> {
    return api.post(`/v1/divesites/media/${assetId}/contribute`, data).then(r => r.data)
  },

  listEncyclopedia(params?: { species_name?: string; species_id?: string; limit?: number; offset?: number }): Promise<{ items: EncyclopediaAsset[]; total: number }> {
    const qs = new URLSearchParams()
    if (params?.species_name) qs.set('species_name', params.species_name)
    if (params?.species_id) qs.set('species_id', params.species_id)
    if (params?.limit) qs.set('limit', String(params.limit))
    if (params?.offset) qs.set('offset', String(params.offset))
    return api.get<{ items: EncyclopediaAsset[]; total: number }>(`/v1/divesites/media/encyclopedia${qs.toString() ? '?' + qs : ''}`).then(r => r.data)
  },
}

export interface UploadUrlResponse {
  assetId: string
  uploadUrl: string
}

export const mediaApi = {
  list(params?: { diveId?: string }): Promise<MediaAsset[]> {
    const qs = params?.diveId ? `?diveId=${params.diveId}` : ''
    return api.get<MediaAsset[]>(`/v1/media/mine${qs}`).then(r => r.data)
  },

  get(id: string): Promise<MediaAsset> {
    return api.get<MediaAsset>(`/v1/media/${id}`).then(r => r.data)
  },

  // Vista pubblica snella (avatar, allegati chat, foto social): leggibile anche
  // se l'asset è di un altro utente. Non espone i metadati privati dell'immersione.
  getPublic(id: string): Promise<MediaAsset> {
    return api.get<MediaAsset>(`/v1/media/${id}/public`).then(r => r.data)
  },

  async requestUploadUrl(file: { name: string; type: string; size: number }): Promise<UploadUrlResponse> {
    const { data } = await api.post<UploadUrlResponse>('/v1/media/upload-url', {
      filename: file.name,
      mimeType: file.type,
      sizeBytes: file.size,
    })
    return data
  },

  async confirm(assetId: string, opts: { enhanceUnderwater?: boolean; capturedAt?: string; depthM?: number } = {}): Promise<MediaAsset> {
    const { data } = await api.post<MediaAsset>('/v1/media/confirm', { assetId, enhanceUnderwater: false, ...opts })
    return data
  },

  async upload(file: File, opts?: { onProgress?: (pct: number) => void }): Promise<MediaAsset> {
    const qs = new URLSearchParams({ filename: file.name, mimeType: file.type, sizeBytes: String(file.size) })
    const { data } = await api.post<MediaAsset>(`/v1/media/upload?${qs}`, file, {
      headers: { 'Content-Type': file.type },
      // Progress reale di upload (prima i vari dropzone simulavano la barra con un timer).
      onUploadProgress: opts?.onProgress
        ? (e) => { if (e.total) opts.onProgress!(Math.round((e.loaded / e.total) * 100)) }
        : undefined,
    })
    return data
  },

  async uploadBlob(blob: Blob, filename: string): Promise<MediaAsset> {
    const qs = new URLSearchParams({ filename, mimeType: blob.type, sizeBytes: String(blob.size) })
    const { data } = await api.post<MediaAsset>(`/v1/media/upload?${qs}`, blob, {
      headers: { 'Content-Type': blob.type },
    })
    return data
  },

  update(id: string, data: {
    depthM?: number | null
    waterTempC?: number | null
    visibilityM?: number | null
    notes?: string | null
    enhanceUnderwater?: boolean
    diveId?: string | null
  }): Promise<MediaAsset> {
    return api.patch<MediaAsset>(`/v1/media/${id}`, data).then(r => r.data)
  },

  delete(id: string): Promise<void> {
    return api.delete(`/v1/media/${id}`).then(() => undefined)
  },
}

// ── Species media asset upload (with ownership consent) ───────────────────────

export interface SpeciesMediaAssetResponse {
  asset_id: string
  is_duplicate: boolean
  url: string
  content_type: string
  file_size_bytes: number | null
  width: number | null
  height: number | null
  consent_granted: boolean
  consent_timestamp: string | null
  upload_date: string | null
  exif: {
    taken_at: string | null
    lat: number | null
    lng: number | null
    altitude_m: number | null
    camera_make: string | null
    camera_model: string | null
    focal_length_mm: number | null
    exposure_time: string | null
    aperture: string | null
    iso: number | null
    flash: string | null
    color_space: string | null
    software: string | null
  }
  perceptual_hash: string | null
}

/**
 * Upload a species photo/video to the central media asset repository.
 * The caller MUST have shown the user the consent dialog and received explicit agreement.
 * consent=true is sent as a form field to confirm this server-side.
 */
export async function uploadSpeciesMediaAsset(file: File): Promise<SpeciesMediaAssetResponse> {
  const form = new FormData()
  form.append('file', file)
  form.append('consent', 'true')
  const response = await api.post<SpeciesMediaAssetResponse>(
    '/v1/divesites/media/upload',
    form,
  )
  return response.data
}
